/**
 * The side panel's width, as arithmetic.
 *
 * The audit measured the panel as a fixed 360-px column that showed about a
 * third of the Mockup panel at 1366x768, so the width is now the shop's to
 * set — but not without limits: below 360 px the ink rows and the `.ctl` grid
 * wrap into nonsense, and past 640 px the panel is reading room nobody needs.
 * The viewer keeps at least 700 px whatever the window does, which is what
 * makes the cap depend on the viewport rather than being a constant.
 *
 * No DOM, no store: everything here is a number in and a number out, so the
 * bounds can be tested without a browser. The panel's stable section ids live here
 * too, for the same reason: they are shared facts about the panel that both a
 * component and the persistence layer have to agree on.
 */

/**
 * The Details drawer's section id, and the one folded state that is NOT a shop
 * preference.
 *
 * A document whose checks fail opens the drawer itself, once, so a first-time user is
 * not left with "1 problem" hiding the sentence that explains it — and that write went
 * straight into the persisted `viewer.collapsed`. After the first file that ever failed
 * a check, the drawer then started OPEN in every later session, for every document,
 * undoing the "starts folded" default the audit asked for. It is left out of what is
 * saved (`persist.ts`), so the drawer starts folded every session and both the
 * auto-open and a hand-fold last exactly as long as the session does.
 */
export const INSPECTOR_SECTION_ID = 'inspector';
/** Section ids whose folded state is session-only rather than a saved preference. */
export const SESSION_ONLY_SECTIONS: ReadonlyArray<string> = [INSPECTOR_SECTION_ID];

export const PANEL_MIN = 360;
export const PANEL_MAX = 640;
export const PANEL_DEFAULT = 440;
/** One arrow-key press on the resizer. */
export const PANEL_STEP = 16;
/** The narrowest viewer the panel may leave behind. */
export const VIEWER_MIN = 700;
/** The file list's fixed column, which is in the window too (`styles.css`:
 *  `grid-template-columns: 240px 1fr var(--panel-w)`). Exported beside the others
 *  because it is part of this arithmetic, not a detail of the stylesheet: without it
 *  the cap said the viewer kept 700 px at 1366 while it actually had 486. */
export const FILES_W = 240;

/**
 * The widest the panel may be in a window this wide — `PANEL_MAX`, less
 * whatever it would have to take from the viewer to get there, and never below
 * `PANEL_MIN`: on a narrow window the panel stays usable and the viewer gives
 * up the space instead, because a panel squeezed to 200 px is not a panel.
 *
 * The window holds three columns, so what is left for the viewer is
 * `viewportW − FILES_W − panel`. At 1366 that caps the panel at 426 rather than the
 * full 640 the old arithmetic allowed — which left a 486-px viewer, the exact
 * "300-px viewer on a laptop" the resizer's own comment says this prevents.
 */
export function maxPanelWidth(viewportW: number): number {
  if (!Number.isFinite(viewportW)) return PANEL_MAX;
  return Math.max(PANEL_MIN, Math.min(PANEL_MAX, viewportW - FILES_W - VIEWER_MIN));
}

/**
 * `w` held to `[PANEL_MIN, maxPanelWidth(viewportW)]`. A width that is not a
 * finite number at all (a corrupt persisted value, a NaN out of a pointer
 * event on a detached element) is not clamped but replaced: there is no
 * sensible nearest value to NaN, and the default is the honest answer.
 */
export function clampPanelWidth(w: number, viewportW: number): number {
  const max = maxPanelWidth(viewportW);
  if (!Number.isFinite(w)) return Math.min(max, PANEL_DEFAULT);
  return Math.min(max, Math.max(PANEL_MIN, w));
}

/** The window's width, or `Infinity` where there is no window (the node tests). */
export function viewportWidth(): number {
  return typeof window === 'undefined' ? Number.POSITIVE_INFINITY : window.innerWidth;
}
